import {
  GridLayoutConfig,
  GridItemPosition,
  TabGroupConfig,
  DEFAULT_COLS,
  DEFAULT_ROWS,
  DEFAULT_WIDGET_H,
  DEFAULT_MIN_W,
  DEFAULT_MIN_H,
  DEFAULT_TAB_GROUP_MIN_W,
  DEFAULT_TAB_GROUP_MIN_H,
  getDefaultGridLayout,
} from '../types/gridLayout';

// Minimal FlexLayout JSON typing for migration
interface FlexNode {
  type: string;
  id?: string;
  name?: string;
  component?: string;
  weight?: number;
  children?: FlexNode[];
}

interface FlexLayoutJson {
  global?: Record<string, unknown>;
  borders?: unknown[];
  layout: FlexNode;
}

interface ExtractedTabset {
  tabs: string[];
  weight: number;
}

// Check if a layout config is the new grid format
export function isGridLayoutConfig(config: Record<string, unknown>): boolean {
  return (config as { version?: number }).version === 2;
}

// Migrate a FlexLayout JSON model to GridLayoutConfig
export function migrateFlexLayoutToGrid(
  rawConfig: Record<string, unknown>,
  validWidgetIds: string[],
): GridLayoutConfig {
  try {
    const flexJson = rawConfig as unknown as FlexLayoutJson;
    if (!flexJson.layout) {
      return getDefaultGridLayout();
    }

    const validIds = new Set(validWidgetIds);

    // Extract tabsets from the FlexLayout tree
    const tabsets: ExtractedTabset[] = [];
    extractTabsets(flexJson.layout, tabsets);

    if (tabsets.length === 0) {
      return getDefaultGridLayout();
    }

    const layout: GridItemPosition[] = [];
    const tabGroups: TabGroupConfig[] = [];
    let currentY = 0;

    // Arrange tabsets — use 2-column layout if many, full width if few
    const useFullWidth = tabsets.length <= 2;

    for (let i = 0; i < tabsets.length; i++) {
      const ts = tabsets[i];
      const validTabs = ts.tabs.filter(id => validIds.has(id));
      if (validTabs.length === 0) continue;

      const w = useFullWidth ? 12 : 6;
      const x = useFullWidth ? 0 : (i % 2) * 6;

      if (!useFullWidth && i % 2 === 0 && i > 0) {
        currentY += DEFAULT_WIDGET_H;
      }

      if (validTabs.length === 1) {
        // Standalone widget
        layout.push({
          i: validTabs[0],
          x,
          y: currentY,
          w,
          h: DEFAULT_WIDGET_H,
          minW: DEFAULT_MIN_W,
          minH: DEFAULT_MIN_H,
        });
      } else {
        // Tab group
        const groupId = `tabgroup-migrated-${i}`;
        tabGroups.push({
          id: groupId,
          widgetIds: validTabs,
          activeWidgetId: validTabs[0],
        });
        layout.push({
          i: groupId,
          x,
          y: currentY,
          w,
          h: DEFAULT_WIDGET_H,
          minW: DEFAULT_TAB_GROUP_MIN_W,
          minH: DEFAULT_TAB_GROUP_MIN_H,
        });
      }

      if (useFullWidth) {
        currentY += DEFAULT_WIDGET_H;
      }
    }

    return {
      version: 2,
      cols: DEFAULT_COLS,
      rows: DEFAULT_ROWS,
      layout,
      tabGroups,
    };
  } catch {
    console.warn('Failed to migrate FlexLayout config, using default grid layout');
    return getDefaultGridLayout();
  }
}

function extractTabsets(node: FlexNode, result: ExtractedTabset[]): void {
  if (node.type === 'tabset') {
    const tabs = (node.children || [])
      .filter(child => child.type === 'tab' && child.id)
      .map(child => child.id!);
    if (tabs.length > 0) {
      result.push({ tabs, weight: node.weight || 50 });
    }
  } else if (node.children) {
    for (const child of node.children) {
      extractTabsets(child, result);
    }
  }
}
